import { useIsMutating } from '@tanstack/react-query';
import { Check, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import {
  Badge,
  Button,
  ChipToggle,
  Dialog,
  ErrorState,
  Field,
  Input,
  LoadingState,
  Select,
  useToast,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { formatRM } from '@/features/billing/money';
import {
  updatePatientKey,
  usePatient,
  usePatientActions,
  usePayers,
  useUpdatePatient,
} from '@/features/patients/api';
import { countryName } from '@/features/patients/countries';
import {
  ASSISTANCE_LABELS,
  CONSENT_LABELS,
  COUNTER_CONSENTS,
  type AssistanceNeed,
  type ConsentType,
  type PatientDetail,
} from '@/features/patients/types';
import { isApiError } from '@/lib/api';
import { CounterEkycDrawer } from '@/pages/queue/CounterEkycDrawer';
import { EkycPhoto } from '@/pages/queue/EkycPhoto';
import { AddToQueue } from './AddToQueue';
import { AllergyList } from './AllergyList';
import { PatientAppAccount } from './PatientAppAccount';
import { PatientBloodType } from './PatientBloodType';
import { PatientCoverages } from './PatientCoverages';
import { MemberPriceStatus } from './MemberPriceStatus';
import { PatientIdentityText } from './PatientIdentityText';
import { PatientPhoto } from './PatientPhoto';
import {
  buildContactPatch,
  CONTACT_LIMITS,
  toContactForm,
  type ContactForm,
} from './patient-contact';
import styles from './patients.module.css';

const CONSENTS: ConsentType[] = COUNTER_CONSENTS;
const NEEDS = Object.keys(ASSISTANCE_LABELS) as AssistanceNeed[];
const FORM_ID = 'patient-details-form';

/**
 * "Details → Patient drawer" (Admin v2 #25): eKYC photo, identity, coverage,
 * eKYC, member price, consents, last visit and queue; phone, bills, consents
 * (health promotions kept separate), assistance needs and a safe contact for
 * patients at risk. Save writes the changes, audited by the API. Identity
 * documents are not edited here (UAT RL-705: a stricter, reviewed workflow).
 */
export function PatientDetailsDrawer({
  patientId,
  onClose,
}: {
  patientId: string | null;
  onClose: () => void;
}) {
  const {
    data: patient,
    isLoading,
    error,
    refetch,
  } = usePatient(patientId ?? undefined);
  const { hasPermission } = useSession();
  const canEdit =
    !!patient &&
    hasPermission('patients:update') &&
    patient.status !== 'merged';
  const saving =
    useIsMutating({ mutationKey: updatePatientKey(patientId ?? '') }) > 0;

  return (
    <Dialog
      open={!!patientId}
      onClose={onClose}
      title={patient?.fullName ?? 'Patient'}
      footer={
        canEdit ? (
          <Button
            type="submit"
            form={FORM_ID}
            variant="primary"
            icon={<Check size={16} />}
            loading={saving}
          >
            Save
          </Button>
        ) : undefined
      }
    >
      {isLoading && <LoadingState />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}
      {patient && (
        // Re-mounts (and re-initialises the form) when a newer version arrives.
        <DetailsForm
          key={`${patient.id}:${patient.version}`}
          patient={patient}
          canEdit={canEdit}
          onConflict={() => void refetch()}
          onLeave={onClose}
        />
      )}
    </Dialog>
  );
}

function DetailsForm({
  patient,
  canEdit,
  onConflict,
  onLeave,
}: {
  patient: PatientDetail;
  canEdit: boolean;
  onConflict: () => void;
  /** Closes the drawer when one of its links opens another page. */
  onLeave: () => void;
}) {
  const update = useUpdatePatient(patient.id);
  const actions = usePatientActions();
  const { data: payers } = usePayers();
  const { hasPermission } = useSession();
  const toast = useToast();
  const { pathname } = useLocation();
  // UAT RL-1041: inside the record page the drawer is already over the full
  // record; a link to the same page did nothing and left the drawer open.
  const onRecordPage = pathname === `/patients/${patient.id}`;
  const safe = patient.contacts.find((c) => c.kind === 'safe_contact');
  const [phone, setPhone] = useState(patient.phone ?? '');
  const [contact, setContact] = useState<ContactForm>(() =>
    toContactForm(patient),
  );
  const [contactErrors, setContactErrors] = useState<
    Partial<Record<keyof ContactForm, string>>
  >({});
  const [consents, setConsents] = useState<ConsentType[]>(
    CONSENTS.filter((c) => patient.consents[c]?.granted),
  );
  const [needs, setNeeds] = useState<AssistanceNeed[]>(patient.assistanceNeeds);
  const [safeOnly, setSafeOnly] = useState(patient.safeContactOnly);
  const [safeName, setSafeName] = useState(safe?.name ?? '');
  const [safePhone, setSafePhone] = useState(safe?.phone ?? '');
  const [ekyc, setEkyc] = useState(false);
  const [cover, setCover] = useState({ payerId: '', memberNo: '' });
  const fail = (err: unknown) =>
    toast.show({
      tone: 'error',
      title: 'Not done',
      body: isApiError(err) ? err.message : undefined,
      error: err,
    });

  async function save() {
    const { patch: contactPatch, errors } = buildContactPatch(patient, contact);
    setContactErrors(errors);
    if (Object.keys(errors).length) return;
    const safeChanged =
      safePhone.trim() !== (safe?.phone ?? '') ||
      safeName.trim() !== (safe?.name ?? '');
    try {
      await update.mutateAsync({
        version: patient.version,
        phone,
        ...contactPatch,
        assistanceNeeds: needs,
        safeContactOnly: safeOnly,
        consents: Object.fromEntries(
          CONSENTS.map((c) => [c, consents.includes(c)]),
        ),
        ...(safeChanged
          ? {
              contacts: {
                removeIds: safe ? [safe.id] : [],
                add: safePhone.trim()
                  ? [
                      {
                        kind: 'safe_contact' as const,
                        name: safeName.trim() || 'Safe contact',
                        relationship: null,
                        phone: safePhone.trim(),
                      },
                    ]
                  : [],
              },
            }
          : {}),
      });
      toast.show({ tone: 'success', title: 'Patient details saved' });
    } catch (err) {
      const conflict = isApiError(err) && err.code === 'VERSION_CONFLICT';
      toast.show({
        tone: 'error',
        title: conflict
          ? 'Someone else changed this patient'
          : 'Could not save',
        body: conflict
          ? 'The latest details were reloaded. Check them and save again.'
          : isApiError(err)
            ? err.message
            : undefined,
      });
      if (isApiError(err) && err.fieldErrors.length)
        setContactErrors(
          Object.fromEntries(
            err.fieldErrors
              .filter((e) => e.field in CONTACT_LIMITS)
              .map((e) => [e.field, e.message]),
          ),
        );
      if (conflict) onConflict();
    }
  }

  const contactInput = (
    key: keyof ContactForm,
    props: Record<string, unknown> = {},
  ) => (
    <Input
      value={contact[key]}
      maxLength={CONTACT_LIMITS[key]}
      onChange={(e) => {
        setContact((c) => ({ ...c, [key]: e.target.value }));
        setContactErrors(({ [key]: _, ...rest }) => rest);
      }}
      disabled={!canEdit}
      {...props}
    />
  );

  const docLabel = patient.identity?.type === 'mykad' ? 'MyKad' : 'Passport';
  const verified = patient.ekyc.status === 'verified';

  return (
    <form
      id={FORM_ID}
      className={styles.drawer}
      // Our own messages (next to each field) instead of the browser's bubble.
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <div className={styles.headline}>
        <EkycPhoto
          name={patient.fullName}
          verifiedAt={patient.ekyc.verifiedAt}
        />
        <div>
          <p className={styles.name}>{patient.fullName}</p>
          <p className={styles.sub}>
            {docLabel} <PatientIdentityText identity={patient.identity} />
          </p>
        </div>
      </div>
      {patient.mergedInto && (
        <p className={styles.note}>
          Merged into {patient.mergedInto.fullName}.{' '}
          <Link to={`/patients/${patient.mergedInto.id}`} onClick={onLeave}>
            Open the kept record
          </Link>
        </p>
      )}
      <dl className={styles.rows}>
        <dt>{docLabel}</dt>
        <dd className="mono">
          <PatientIdentityText identity={patient.identity} />
        </dd>
        <dt>Nationality</dt>
        <dd>{countryName(patient.nationality)}</dd>
        <dt>Phone</dt>
        <dd>{patient.phone ?? '—'}</dd>
        <dt>Coverage</dt>
        <dd>
          <PatientCoverages patient={patient} />
        </dd>
        <dt>eKYC</dt>
        <dd>
          <Badge tone={verified ? 'success' : 'warning'}>
            {verified ? 'Verified' : 'Unverified'}
          </Badge>
        </dd>
        <dt>App account</dt>
        <dd>
          <PatientAppAccount patient={patient} />
        </dd>
        <dt>Photo</dt>
        <dd>
          <PatientPhoto patient={patient} />
        </dd>
        <dt>Blood type</dt>
        <dd>
          <PatientBloodType patient={patient} />
        </dd>
        <dt>Member price</dt>
        <dd>
          <MemberPriceStatus patient={patient} />
        </dd>
        <dt>Consents</dt>
        <dd>
          {CONSENTS.filter((c) => patient.consents[c]?.granted)
            .map((c) => CONSENT_LABELS[c])
            .join(', ') || 'None recorded'}
        </dd>
        <dt>Last visit</dt>
        <dd>{patient.lastVisitAt ? visitDay(patient.lastVisitAt) : '—'}</dd>
        <dt>In queue</dt>
        <dd>
          {patient.inQueue
            ? `${patient.inQueue.number} · ${patient.inQueue.status === 'in_room' ? `in ${patient.inQueue.room ?? 'room'}` : 'waiting'}`
            : '—'}
        </dd>
        {patient.allergies.length > 0 && (
          <>
            <dt>Allergies</dt>
            <dd>
              <AllergyList allergies={patient.allergies} />
            </dd>
          </>
        )}
        {patient.arrivalPassToken && (
          <>
            <dt>Arrival pass</dt>
            <dd>
              <span className="mono">{patient.arrivalPassToken}</span>{' '}
              <span className={styles.sub}>
                (test token: no patient app yet)
              </span>
            </dd>
          </>
        )}
      </dl>
      <AddToQueue key={patient.id} patient={patient} />
      {!verified &&
        hasPermission('patients:update') &&
        patient.status === 'active' && (
          <Button
            type="button"
            icon={<ShieldCheck size={16} />}
            onClick={() => setEkyc(true)}
          >
            Verify eKYC at the counter
          </Button>
        )}

      <Field label="Phone">
        <Input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={!canEdit}
        />
      </Field>

      <div className={styles.section}>
        <p className="eyebrow">Email and address</p>
        <Field
          label="Email (optional)"
          hint={
            patient.email
              ? 'Can be changed but not removed here.'
              : 'Used for receipts and notices.'
          }
          error={contactErrors.email}
        >
          {contactInput('email', { type: 'email', autoComplete: 'off' })}
        </Field>
        <Field label="Address line 1" error={contactErrors.addressLine1}>
          {contactInput('addressLine1')}
        </Field>
        <Field
          label="Address line 2 (optional)"
          error={contactErrors.addressLine2}
        >
          {contactInput('addressLine2')}
        </Field>
        <div className={styles.split}>
          <Field label="Postcode" error={contactErrors.postcode}>
            {contactInput('postcode', { inputMode: 'numeric' })}
          </Field>
          <Field label="City" error={contactErrors.city}>
            {contactInput('city')}
          </Field>
        </div>
        <Field label="State" error={contactErrors.state}>
          {contactInput('state')}
        </Field>
      </div>

      <div className={styles.section}>
        <p className="eyebrow">Bills</p>
        {patient.bills.length === 0 && <p className={styles.sub}>No bills.</p>}
        <ul className={styles.billList}>
          {patient.bills.map((b) => (
            <li key={b.id}>
              <span className="mono">{b.number}</span>
              <span>{b.payer?.name ?? 'Self'}</span>
              <span>
                {formatRM(b.totalSen)}{' '}
                <Badge tone={b.status === 'open' ? 'warning' : 'neutral'}>
                  {b.status === 'open'
                    ? 'Open'
                    : b.status === 'closed'
                      ? 'Paid'
                      : 'Void'}
                </Badge>
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.section}>
        <p className="eyebrow">Consents</p>
        <ChipToggle
          label="Consents"
          options={CONSENTS.map((c) => ({
            value: c,
            label: CONSENT_LABELS[c],
          }))}
          selected={consents}
          onChange={setConsents}
          disabled={!canEdit}
        />
        <p className={styles.note}>
          Marketing is kept separate: health promotions only go to patients who
          opt in, and every message has a way to stop.
        </p>
      </div>
      <div className={styles.section}>
        <p className="eyebrow">Assistance needs</p>
        <ChipToggle
          label="Assistance needs"
          options={NEEDS.map((n) => ({
            value: n,
            label: ASSISTANCE_LABELS[n],
          }))}
          selected={needs}
          onChange={setNeeds}
          disabled={!canEdit}
        />
      </div>
      <div className={styles.section}>
        <p className="eyebrow">Safe contact</p>
        <ChipToggle
          label="Safe contact"
          single
          options={[
            { value: 'normal', label: 'Normal contact' },
            {
              value: 'safe',
              label: 'Do not send messages; call only the safe number',
            },
          ]}
          selected={[safeOnly ? 'safe' : 'normal']}
          onChange={([v]) => setSafeOnly(v === 'safe')}
          disabled={!canEdit}
        />
        <div className={styles.split}>
          <Field label="Safe contact name">
            <Input
              value={safeName}
              onChange={(e) => setSafeName(e.target.value)}
              disabled={!canEdit}
            />
          </Field>
          <Field label="Safe number">
            <Input
              value={safePhone}
              onChange={(e) => setSafePhone(e.target.value)}
              disabled={!canEdit}
            />
          </Field>
        </div>
        <p className={styles.note}>
          For patients at risk: with the safe-contact setting no app message or
          text is sent, so nothing reaches a shared phone.
        </p>
      </div>

      {hasPermission('patients:update') && patient.status === 'active' && (
        <div className={styles.section}>
          <p className="eyebrow">Add a panel membership</p>
          <div className={styles.split}>
            <Select
              aria-label="Panel"
              value={cover.payerId}
              placeholder="Panel or insurer"
              onChange={(e) => setCover({ ...cover, payerId: e.target.value })}
              options={(payers ?? []).map((p) => ({
                value: p.id,
                label: p.name,
              }))}
            />
            <Input
              aria-label="Member or GL number"
              placeholder="Member / GL number"
              value={cover.memberNo}
              onChange={(e) => setCover({ ...cover, memberNo: e.target.value })}
            />
          </div>
          <Button
            type="button"
            size="sm"
            disabled={!cover.payerId || cover.memberNo.trim().length < 2}
            loading={actions.addCoverage.isPending}
            onClick={() =>
              actions.addCoverage
                .mutateAsync({
                  id: patient.id,
                  payerId: cover.payerId,
                  memberNo: cover.memberNo.trim(),
                })
                .then(() => setCover({ payerId: '', memberNo: '' }))
                .catch(fail)
            }
          >
            Add membership
          </Button>
          {patient.coverages.map((c) => (
            <p key={c.id} className={styles.sub}>
              {c.payer.name} {c.memberNo}{' '}
              <button
                type="button"
                className={styles.inlineLink}
                onClick={() =>
                  actions.endCoverage
                    .mutateAsync({ id: patient.id, coverageId: c.id })
                    .catch(fail)
                }
              >
                End
              </button>
            </p>
          ))}
        </div>
      )}

      {!onRecordPage && (
        <Link
          to={`/patients/${patient.id}`}
          className={styles.recordLink}
          onClick={onLeave}
        >
          Open the full record
        </Link>
      )}
      <CounterEkycDrawer
        patient={ekyc ? patient : null}
        onClose={() => setEkyc(false)}
      />
    </form>
  );
}

const visitDay = (iso: string) => {
  const d = new Date(iso);
  const today = new Date();
  return d.toDateString() === today.toDateString()
    ? 'Today'
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};
